/**
 * The rundown row kinds and the fields each kind carries (PLAN §5.1, §7.1a).
 *
 * **One zod object per kind is the single source.** From it come:
 *   - the typed view of a row's fields (`readFields`), with defaults for
 *     fields never written — the engine, the SPA and the handlers read this;
 *   - the per-field validator ingress uses on a patch (`fieldSpecFor`), so a
 *     field is valid in a patch exactly when the typed view could hold it.
 *
 * **How a kind's fields map onto LWW registers** (`lww.ts`, one HLC per field
 * path). Three shapes:
 *   - **atomic** fields are one register each, whatever their value's shape.
 *     `enable`, `timing`, `inTransition` are objects but are written whole: their
 *     members only make sense together (a `back-time` timing without its
 *     `expectedEnd` is not a timing), and per-member registers would let two
 *     concurrent edits merge into a combination neither user wrote.
 *   - **open namespaces** (`params`) are a register per dotted leaf
 *     (`params.clip`, `params.vs.in`), so concurrent edits to two parameters
 *     of one piece both survive (PLAN §6.1). The namespace itself may not be
 *     written whole — a wholesale write and a leaf write would be two registers
 *     for overlapping data, and neither would win. The piece type (studio
 *     config, D22) will validate the leaves; here any JSON value is accepted.
 *   - **text** fields hold a Yjs document (`text.ts`); `f.<field>` is its
 *     materialised string, never written directly.
 *
 * `parentId`, `ord` and `deleted` are ordinary registers too (PLAN §6.1: "also
 * LWW fields (move = one field write)"), common to every kind.
 *
 * Unknown fields — written by a newer version (PLAN §6.8) — are preserved in
 * the row by the merge; the typed view simply does not show them.
 */
import { z } from 'zod';
import { durationMsSchema, epochMsSchema, jsonValueSchema, type JsonValue, ordSchema, ulidSchema } from './primitives.js';

export const ROW_KINDS = ['playlist', 'rundown', 'segment', 'part', 'piece', 'adlib'] as const;
export const rowKindSchema = z.enum(ROW_KINDS);
export type RowKind = z.infer<typeof rowKindSchema>;

/**
 * Which kinds a row of each kind may hang under (PLAN §5.1, D4; §6.4's
 * "`kind:'piece'` must have a part parent"). An adlib is part-scoped (under a
 * part) or global (under the rundown). A rundown sits in a playlist, or stands
 * alone; a playlist is always a root.
 */
export const ALLOWED_PARENT_KINDS: Readonly<Record<RowKind, readonly RowKind[]>> = {
  playlist: [],
  rundown: ['playlist'],
  segment: ['rundown'],
  part: ['segment'],
  piece: ['part'],
  adlib: ['part', 'rundown'],
};

/** Kinds whose `parentId` may be null (or name a row outside the loaded set). */
export const ROOT_KINDS: readonly RowKind[] = ['playlist', 'rundown'];

/**
 * Sofie's piece lifespans, verbatim (`PieceLifespan` in sofie-core 26.x,
 * PLAN §5.1). `segment-end`, `rundown-end` and `showstyle-end` resolve from
 * the rundown order and resume after an override; `segment-change` and
 * `rundown-change` track the playhead.
 */
export const PIECE_LIFESPANS = [
  'part-only',
  'segment-change',
  'segment-end',
  'rundown-change',
  'rundown-end',
  'showstyle-end',
] as const;
export const pieceLifespanSchema = z.enum(PIECE_LIFESPANS);
export type PieceLifespan = z.infer<typeof pieceLifespanSchema>;

const nameSchema = z.string().max(500);
const layerIdSchema = z.string().max(128);

/** PLAN §5.1: `enable {start: ms|'now', duration?}`, relative to the part start. */
export const pieceEnableSchema = z
  .object({
    start: z.union([durationMsSchema, z.literal('now')]),
    duration: durationMsSchema.nullable(),
  })
  .strict();
export type PieceEnable = z.infer<typeof pieceEnableSchema>;

/** PLAN §5.1: `inTransition {blockTakeDuration, previousPartKeepalive, contentDelay}`. */
export const inTransitionSchema = z
  .object({
    blockTakeDuration: durationMsSchema,
    previousPartKeepalive: durationMsSchema,
    contentDelay: durationMsSchema,
  })
  .strict();

/** PLAN §5.1: `outTransition {duration}`. */
export const outTransitionSchema = z.object({ duration: durationMsSchema }).strict();

/**
 * Playlist timing (PLAN §5.1: `none | forward-time(expectedStart) |
 * back-time(expectedEnd) | duration`), as Sofie's `RundownPlaylistTiming`:
 * the mode's anchor is required, the other two values are optional hints.
 */
export const playlistTimingSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('none') }).strict(),
  z
    .object({
      type: z.literal('forward-time'),
      expectedStart: epochMsSchema,
      expectedDuration: durationMsSchema.nullable(),
      expectedEnd: epochMsSchema.nullable(),
    })
    .strict(),
  z
    .object({
      type: z.literal('back-time'),
      expectedEnd: epochMsSchema,
      expectedStart: epochMsSchema.nullable(),
      expectedDuration: durationMsSchema.nullable(),
    })
    .strict(),
  z
    .object({
      type: z.literal('duration'),
      expectedDuration: durationMsSchema,
      expectedEnd: epochMsSchema.nullable(),
    })
    .strict(),
]);
export type PlaylistTiming = z.infer<typeof playlistTimingSchema>;

const openParamsSchema = z.record(z.string(), jsonValueSchema);

/** The registers every kind has. */
const structuralFields = {
  parentId: ulidSchema.nullable().default(null),
  ord: ordSchema.default('a0'),
  deleted: z.boolean().default(false),
};

/**
 * The schema version this code writes into a new rundown (PLAN §6.8). A
 * breaking change bumps it and ships a migration copy.
 */
export const CURRENT_SCHEMA_VERSION = 1;

/**
 * The studio a playlist or rundown is bound to (null: none) — the studio id
 * alphabet of `@acp/api-contract/studios.js`. The binding decides which
 * on-prem site replicates the partition (a site replicates the partitions
 * bound to its studios, and those its productions run: docs/sitelink.md);
 * it is not yet a permission scope (`@acp/handlers/access.js`' TODO).
 */
export const boundStudioSchema = z.string().regex(/^[A-Za-z0-9][A-Za-z0-9_-]{0,63}$/).nullable();

export const playlistFieldsSchema = z.object({
  ...structuralFields,
  name: nameSchema.default(''),
  timing: playlistTimingSchema.default({ type: 'none' }),
  studioId: boundStudioSchema.default(null),
});

export const rundownFieldsSchema = z.object({
  ...structuralFields,
  name: nameSchema.default(''),
  schemaVersion: z.number().int().positive().default(CURRENT_SCHEMA_VERSION),
  notes: z.string().default(''),
  studioId: boundStudioSchema.default(null),
});

export const segmentFieldsSchema = z.object({
  ...structuralFields,
  name: nameSchema.default(''),
  budgetDuration: durationMsSchema.nullable().default(null),
  hidden: z.boolean().default(false),
});

export const partFieldsSchema = z.object({
  ...structuralFields,
  title: nameSchema.default(''),
  expectedDuration: durationMsSchema.nullable().default(null),
  autoNext: z.boolean().default(false),
  autoNextOverlap: durationMsSchema.default(0),
  untimed: z.boolean().default(false),
  invalid: z.boolean().default(false),
  invalidReason: z.string().max(1000).nullable().default(null),
  floated: z.boolean().default(false),
  inTransition: inTransitionSchema.nullable().default(null),
  outTransition: outTransitionSchema.nullable().default(null),
  script: z.string().default(''),
  notes: z.string().default(''),
});

const pieceLikeFields = {
  name: nameSchema.default(''),
  pieceType: z.string().max(128).default(''),
  sourceLayer: layerIdSchema.default(''),
  outputLayer: layerIdSchema.default(''),
  params: openParamsSchema.default({}),
  prerollDuration: durationMsSchema.default(0),
  postrollDuration: durationMsSchema.default(0),
  lifespan: pieceLifespanSchema.default('part-only'),
};

export const pieceFieldsSchema = z.object({
  ...structuralFields,
  ...pieceLikeFields,
  enable: pieceEnableSchema.default({ start: 0, duration: null }),
  virtual: z.boolean().default(false),
});

export const adlibFieldsSchema = z.object({
  ...structuralFields,
  ...pieceLikeFields,
  /** How long the piece runs once played; null = until something ends it. */
  expectedDuration: durationMsSchema.nullable().default(null),
});

/** The typed-view schema per kind. */
export const FIELD_SCHEMAS = {
  playlist: playlistFieldsSchema,
  rundown: rundownFieldsSchema,
  segment: segmentFieldsSchema,
  part: partFieldsSchema,
  piece: pieceFieldsSchema,
  adlib: adlibFieldsSchema,
} as const satisfies Record<RowKind, z.ZodObject>;

export type PlaylistFields = z.infer<typeof playlistFieldsSchema>;
export type RundownFields = z.infer<typeof rundownFieldsSchema>;
export type SegmentFields = z.infer<typeof segmentFieldsSchema>;
export type PartFields = z.infer<typeof partFieldsSchema>;
export type PieceFields = z.infer<typeof pieceFieldsSchema>;
export type AdlibFields = z.infer<typeof adlibFieldsSchema>;
export type FieldsOf<K extends RowKind> = z.infer<(typeof FIELD_SCHEMAS)[K]>;

/** Text fields per kind: Yjs-backed, written only through `patch.text`. */
export const TEXT_FIELDS: Readonly<Record<RowKind, readonly string[]>> = {
  playlist: [],
  rundown: ['notes'],
  segment: [],
  part: ['script', 'notes'],
  piece: [],
  adlib: [],
};

/** Open namespaces per kind: written per dotted leaf, never whole. */
export const OPEN_NAMESPACES: Readonly<Record<RowKind, readonly string[]>> = {
  playlist: [],
  rundown: [],
  segment: [],
  part: [],
  piece: ['params'],
  adlib: ['params'],
};

/** What ingress may do with a field path on a kind. */
export type FieldSpec =
  | { readonly type: 'value'; readonly schema: z.core.$ZodType }
  | { readonly type: 'text' }
  | { readonly type: 'namespace-whole' }
  | { readonly type: 'unknown' };

/** The schema a patch value is checked against: the field's, minus its read default. */
function innerSchema(schema: z.core.$ZodType): z.core.$ZodType {
  return schema instanceof z.ZodDefault ? innerSchema(schema.unwrap()) : schema;
}

/**
 * The spec for one field path on one kind: a value schema, a text field, a
 * wholesale write to an open namespace (always rejected), or unknown.
 */
export function fieldSpecFor(kind: RowKind, path: string): FieldSpec {
  const head = path.split('.')[0] ?? path;
  if (OPEN_NAMESPACES[kind].includes(head)) {
    return head === path ? { type: 'namespace-whole' } : { type: 'value', schema: jsonValueSchema };
  }
  if (TEXT_FIELDS[kind].includes(path)) return { type: 'text' };
  const shape: Readonly<Record<string, z.ZodType>> = FIELD_SCHEMAS[kind].shape;
  if (head !== path || !Object.hasOwn(shape, path)) return { type: 'unknown' };
  const field = shape[path];
  return field === undefined ? { type: 'unknown' } : { type: 'value', schema: innerSchema(field) };
}

/**
 * The value a field reads as before anything wrote it — the typed view's
 * default. `null` for a namespace leaf or an unknown field. Undo uses it to
 * "restore" a field that had never been written: LWW has no unset, so the
 * closest restoration is the value the field read as.
 */
export function defaultValueFor(kind: RowKind, path: string): JsonValue {
  const shape: Readonly<Record<string, z.ZodType>> = FIELD_SCHEMAS[kind].shape;
  const field = Object.hasOwn(shape, path) ? shape[path] : undefined;
  if (field === undefined) return null;
  const parsed = jsonValueSchema.safeParse(z.safeParse(field, undefined).data);
  return parsed.success ? parsed.data : null;
}

/** Whether `path` is a text field on `kind`. */
export function isTextField(kind: RowKind, path: string): boolean {
  return TEXT_FIELDS[kind].includes(path);
}

/**
 * Expand flat dotted paths into nested objects (`params.vs.clip` →
 * `{params: {vs: {clip}}}`). Paths are applied in sorted order, so a prefix
 * (`params`) is placed before its leaves and a leaf replaces a non-object
 * prefix value — deterministic whatever order `f` was built in.
 */
export function expandFieldPaths(f: Readonly<Record<string, unknown>>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const path of Object.keys(f).sort()) {
    const segments = path.split('.');
    const last = segments.pop();
    if (last === undefined) continue;
    let target = out;
    for (const segment of segments) {
      const existing = target[segment];
      // Copied, never reused: `existing` may be a value from `f` itself, and writing a
      // deeper leaf into it would mutate the row being read.
      const next: Record<string, unknown> = isPlainObject(existing) ? { ...existing } : {};
      target[segment] = next;
      target = next;
    }
    target[last] = f[path];
  }
  return out;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** A row's fields in their typed view, discriminated on the kind. */
export type TypedFields = { [K in RowKind]: { readonly kind: K; readonly fields: FieldsOf<K> } }[RowKind];

export type ReadFieldsResult =
  | { readonly ok: true; readonly value: TypedFields }
  | { readonly ok: false; readonly error: string };

function finish<K extends RowKind, T>(
  kind: K,
  result: z.ZodSafeParseResult<T>,
): { readonly ok: true; readonly value: { readonly kind: K; readonly fields: T } } | { readonly ok: false; readonly error: string } {
  if (!result.success) return { ok: false, error: z.prettifyError(result.error) };
  return { ok: true, value: { kind, fields: result.data } };
}

/**
 * The typed view of a row's fields, defaults filled in. Never throws: stored
 * data that fails its schema (written by a buggy or newer writer) is reported,
 * and the engine marks such a part `invalid` rather than crashing (PLAN §6.4).
 */
export function readFields(kind: RowKind, f: Readonly<Record<string, unknown>>): ReadFieldsResult {
  const input = expandFieldPaths(f);
  switch (kind) {
    case 'playlist':
      return finish(kind, playlistFieldsSchema.safeParse(input));
    case 'rundown':
      return finish(kind, rundownFieldsSchema.safeParse(input));
    case 'segment':
      return finish(kind, segmentFieldsSchema.safeParse(input));
    case 'part':
      return finish(kind, partFieldsSchema.safeParse(input));
    case 'piece':
      return finish(kind, pieceFieldsSchema.safeParse(input));
    case 'adlib':
      return finish(kind, adlibFieldsSchema.safeParse(input));
  }
}
